import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { APIData, PopupData, RequestError, Service } from 'webapp-types';
import {
	BillingAddonType,
	BillingCycle,
	BillingServiceType,
	PlanExtrasType,
	PlanType,
	PopupEvent,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { capitalize, isLessThan90Days } from 'webapp/utils/common';
import { getFormattedPrice, getFormattedTime } from 'webapp/utils/number';

import AppNotificationsPausedOrDiscountedComponent from 'webapp/components/app-notifications/paused-or-discounted';
import BarLoaderComponent from 'webapp/components/bar-loader';
import type { PreviewItem } from 'webapp/components/billing/preview-item';
import BillingPreviewItemComponent from 'webapp/components/billing/preview-item';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanLinkComponent from 'webapp/components/common/upgrade-plan-link';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type BillingPlan from 'webapp/models-custom/billing-plan';
import type { NumericAndFormattedPrice } from 'webapp/models-custom/billing-plan';
import type Promotion from 'webapp/models-custom/promotion';

export interface ExtraItem {
	id: string;
	priceId: string;
	isFree?: boolean;
}

interface PreviewItemList extends LabelValuePairString {
	label2: string;
	extra: {
		price: number;
		monthsInCycle: number;
		cycleSavings: number;
		cycleSavingsFormatted: string;
		priceMonthly: number;
		monthlySavings: number;
	};
}

export default class BillingPopupConfirmComponent extends PopupInnerModalComponent<PopupData.Billing.Confirm> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('flow/cancellation') declare flowCancellationService: Service.Flow.Cancellation;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('flow/promotions')
	declare flowPromotionsService: Service.Flow.Promotions;
	@service('timer') declare timerService: Service.Timer;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked planId = '';
	@tracked plan!: BillingPlan;
	@tracked selectedAddonIds: string[] = [];
	@tracked override submitInProgress = false;
	@tracked requestInProgress = false;
	@tracked subcriptionPreview?: APIData.Billing.SubscriptionPreview;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.planId = this.popupData.model.id;
		this.plan = this.popupData.model;

		this.analyticsService.event(TrackingEvent.SUBSCRIPTION_CHANGE_POPUP, {
			origin: this.popupData.trackingOrigin,
			type: this.billingService.getTrackingSubscriptionType(),
		});

		this.args.model.on(PopupEvent.CLOSING, this, () => {
			if (!this.submitInProgress) {
				this.close();
			}
		});

		// Listen for CLOSE_INTENT to block closing if request is in progress
		this.args.model.on(PopupEvent.CLOSE_INTENT, this, () => this.requestInProgress);

		next(this, this.requestSubscriptionPreviewData);
	}

	get isSelectedCurrentActivePlan(): boolean {
		const { finalPlan, isExpired, isCanceled } = this.billingService;

		return !isExpired && !isCanceled && this.plan.id === finalPlan?.id;
	}

	get isConfirmDisabled(): boolean {
		return this.isSelectedCurrentActivePlan && !this.selectedAddonIds.length;
	}

	get isPeriodChange(): boolean {
		const plan = this.plan;

		return (
			this.billingService.isCurrentPlan(plan) ||
			this.billingService.isSamePlanCycleChange(plan)
		);
	}

	get willProrate(): boolean {
		return !this.isPeriodChange && this.billingService.isPlanUpgrade(this.plan);
	}

	get formattedNextPaymentDate() {
		const subcriptionPreviewForCycle = this.subcriptionPreviewPricesForCycle,
			nextPaymentTs = subcriptionPreviewForCycle?.nextInvoiceDate;

		let time = '';
		if (nextPaymentTs) {
			time = getFormattedTime(nextPaymentTs, this.currentUserService.model?.timezone);
		}
		return time;
	}

	get formattedTodayDate() {
		return getFormattedTime(moment().unix(), this.currentUserService.model?.timezone);
	}

	get plansToShow(): BillingPlan[] {
		// all plans from the same planType (diff only billing cycle)
		return this.billingService.availablePlans.filter(
			(item) => item.planType === this.plan.planType,
		);
	}

	get keepDiscountAndCustomizationsOnPlan(): boolean {
		return this.billingService.keepDiscountAndCustomizationsOnPlan(this.plan);
	}

	get sourcesWillBePayingFor(): number {
		return this.keepDiscountAndCustomizationsOnPlan
			? this.billingService.sourcesPayingFor
			: this.billingService.sourcesPayingForWithoutCustomizations;
	}

	get pricingOptionsList(): PreviewItemList[] {
		const plansToShow = this.plansToShow,
			sourcesPayingFor = this.sourcesWillBePayingFor,
			optionsList: PreviewItemList[] = [],
			subcriptionPreview = this.subcriptionPreview,
			keepDiscountAndCustomizationsOnPlan = this.keepDiscountAndCustomizationsOnPlan,
			monthlyDiscoutWithException = this.billingService.monthlyDiscount;

		plansToShow.forEach((plan) => {
			let price: NumericAndFormattedPrice;
			if (keepDiscountAndCustomizationsOnPlan) {
				price = plan.getNumericAndFormattedTotalPriceWithSourcesAndDiscount(
					sourcesPayingFor,
					monthlyDiscoutWithException,
				);
			} else {
				price = plan.getNumericAndFormattedTotalPriceWithSources(sourcesPayingFor);
			}

			const cycleSavings = subcriptionPreview?.savings[plan.billingCycle] || 0,
				cycleSavingsFormatted = getFormattedPrice(cycleSavings);

			optionsList.push({
				label: capitalize(plan.billingCycle),
				label2: cycleSavings ? `Save ${cycleSavingsFormatted}` : '',
				value: plan.id,
				extra: {
					price: price.price,
					monthsInCycle: plan.monthsInCycle,
					cycleSavings: cycleSavings,
					cycleSavingsFormatted: cycleSavingsFormatted,
					priceMonthly: price.price / plan.monthsInCycle,
					monthlySavings: 0,
				},
			});
		});

		return optionsList;
	}

	get selectedPricingOption() {
		return this.pricingOptionsList.find((o) => o.value === this.planId);
	}

	get hasAnyPurchasedAddons() {
		return !!this.billingService.purchasedAddonIds.length;
	}

	get showBilingCycleChangeText() {
		return !this.isSelectedCurrentActivePlan && this.hasAnyPurchasedAddons;
	}

	get availableDiscountPromotions() {
		const promotions = [];
		if (this.plan.planType === PlanType.GROWTH) {
			const growth2024 =
				this.flowPromotionsService.promotions.growthPlanDiscountOptionPromotion;
			if (growth2024) {
				promotions.push(growth2024);
			}
		}

		return promotions;
	}

	get selectedDiscountPromotion(): Promotion | undefined {
		return this.availableDiscountPromotions[0]; // select the first one if any, since there's up to one promotion available currently
	}

	get selectedDiscountPromotionCycleDescription() {
		return this.selectedDiscountPromotion?.billingDescriptions?.[this.plan.billingCycle] ?? '';
	}

	get totalSavingsWithDiscountPromotion() {
		const selectedPricingOption = this.selectedPricingOption,
			normalMonthlySavings = selectedPricingOption?.extra.monthlySavings ?? 0,
			monthsInCycle = selectedPricingOption?.extra.monthsInCycle ?? 0,
			selectedDiscountPromotion = this.selectedDiscountPromotion;

		if (selectedDiscountPromotion) {
			const monthsInPromotion = selectedDiscountPromotion.discountDurationInMonths;

			return (
				selectedDiscountPromotion.totalDiscountAmmount +
				Math.max(monthsInCycle, monthsInPromotion) * normalMonthlySavings
			);
		} else {
			return 0;
		}
	}

	get totalSavingsWithDiscountPromotionFormatted(): string {
		return getFormattedPrice(this.totalSavingsWithDiscountPromotion);
	}

	get invoiceItems(): PreviewItem[] {
		const subcriptionPreviewForCycle = this.subcriptionPreviewPricesForCycle;

		if (!subcriptionPreviewForCycle) {
			return [];
		}

		const sortFunc = (
			previewItemA: APIData.Billing.SubscriptionPricePreviewItem,
			previewItemB: APIData.Billing.SubscriptionPricePreviewItem,
		) => {
			const typeOrder: Partial<
				Record<APIData.Billing.SubscriptionPricePreviewItem['type'], number>
			> = {
				plan: 0,
				[PlanExtrasType.ADD_ON]: 1,
				[PlanExtrasType.EXTENSION]: 2,
			};
			const typeA = previewItemA.type;
			const typeB = previewItemB.type;
			return (typeOrder[typeA] ?? NaN) - (typeOrder[typeB] ?? NaN); // unlisted types compare as NaN, as before
		};

		const is2026SeptBillingGroup = this.billingService.is2026SeptBillingGroup;

		return subcriptionPreviewForCycle.lineItems
			.filter((item) => item.type !== 'discount')
			.sort(sortFunc)
			.map((item) => {
				const cyclePrice = this.getCyclePrice(item.price);

				return {
					name: item.name,
					priceFormatted: item.formattedPrice,
					monthlyCyclePriceFormatted: getFormattedPrice(cyclePrice),
					type: item.type,
					quantity: item.quantity,
					subtitle:
						item.type === 'plan' && !is2026SeptBillingGroup
							? `(${this.plan.freeSourcesFormatted} data sources included)`
							: '',
				};
			});
	}

	get isEligibleForFreeOffer() {
		const activePlan = this.billingService.activePlan,
			createdTimestamp = this.currentUserService.model?.created || 0;

		return (
			activePlan?.billingCycle !== BillingCycle.YEARLY &&
			(activePlan?.isFree || isLessThan90Days(createdTimestamp))
		); // less than 90 days since account creation
	}

	get addonItems(): PreviewItem[] {
		const plan = this.plan,
			addonsToShow = [
				BillingAddonType.WHITE_LABELING,
				BillingAddonType.FISCAL_CALENDARS,
				BillingAddonType.ADVANCED_SECURITY,
			];

		const addons = this.billingService
				.prepareAddonsListForPlan(plan)
				.filter((addon) => addonsToShow.includes(addon.id) && !addon.isEnabled),
			billingCycle = this.plan.billingCycle;

		return addons.map((addon) => {
			const price = addon.prices[billingCycle].price || 0,
				cyclePrice = this.getCyclePrice(price);

			let isMostPopular = false;
			if (this.accountService.isAgency) {
				isMostPopular = addon.id === BillingAddonType.WHITE_LABELING;
			} else {
				isMostPopular = addon.id === BillingAddonType.ADVANCED_SECURITY;
			}

			return {
				id: addon.id,
				icon: addon.icon,
				name: addon.name,
				type: PlanExtrasType.ADD_ON,
				quantity: addon.quantity || 1,
				priceFormatted: getFormattedPrice(price),
				monthlyCyclePriceFormatted: getFormattedPrice(cyclePrice),
				isMostPopular,
			};
		});
	}

	get serviceItems(): PreviewItem[] {
		// "OFFERS" of services when changing plans (like guided onboarding was)
		// example kept for possible later use

		// const plan = this.plan;

		// if (
		// 	this.isEligibleForFreeOffer &&
		// 	(plan?.isProfessional || plan?.isGrowth) &&
		// 	plan.billingCycle === BillingCycle.YEARLY
		// ) {
		// 	const guidedOnboarding = this.billingService.getServiceInfo(
		// 		BillingServiceType.GUIDED_ONBOARDING,
		// 	);

		// 	if (guidedOnboarding) {
		// 		return [
		// 			{
		// 				id: guidedOnboarding.id,
		// 				icon: guidedOnboarding.icon,
		// 				name: guidedOnboarding.name,
		// 				priceFormatted: guidedOnboarding.priceFormatted || '',
		// 				type: 'one_time_service',
		// 				quantity: 1,
		// 				isFree: true,
		// 			},
		// 		];
		// 	}
		// }

		return [];
	}

	get discountItems(): PreviewItem[] {
		const subcriptionPreviewForCycle = this.subcriptionPreviewPricesForCycle;

		if (!subcriptionPreviewForCycle) {
			return [];
		}

		return subcriptionPreviewForCycle.lineItems
			.filter((item) => item.type === 'discount')
			.map((item) => {
				return {
					name: item.name,
					priceFormatted: item.formattedPrice,
					type: item.type,
					quantity: item.quantity,
				};
			});
	}

	get subcriptionPreviewPricesForCycle() {
		return this.subcriptionPreview?.prices[this.plan.billingCycle];
	}

	get pricesFormatted() {
		const subcriptionPreviewForCycle = this.subcriptionPreviewPricesForCycle,
			balance = subcriptionPreviewForCycle?.balance,
			total = subcriptionPreviewForCycle?.total,
			dueNow = subcriptionPreviewForCycle?.amountDue,
			proration = subcriptionPreviewForCycle?.proration;

		const prices: { total?: string; balance?: string; dueNow?: string; proration?: string } =
			{};

		if (typeof total !== 'undefined') {
			prices.total = getFormattedPrice(total, 2);
		}

		if (balance) {
			prices.balance = getFormattedPrice(balance, 2);
		}

		if (typeof dueNow !== 'undefined' && dueNow !== total) {
			prices.dueNow = getFormattedPrice(dueNow, 2);
		}

		if (proration) {
			prices.proration = getFormattedPrice(proration);
		}

		return prices;
	}

	getCyclePrice(price: number): number {
		const billingCycle = this.plan.billingCycle,
			monthsInCycle =
				billingCycle === BillingCycle.YEARLY
					? 12
					: billingCycle === BillingCycle.QUARTERLY
						? 3
						: 1;

		return price / monthsInCycle;
	}

	@action
	changePlan(value: AnySimpleValue) {
		const planId = value as string;
		this.planId = planId;
		this.plan = this.billingService.findPlan(planId)!;

		this.scheduleReposition();
	}

	@action
	confirm() {
		const plan = this.billingService.findPlan(this.planId);

		if (!this.submitInProgress && plan) {
			const selectedAddonObjects: ExtraItem[] = this.selectedAddonIds.map((addonId) => {
					const productPrice = plan.getAddonProductPrice(addonId as BillingAddonType);

					return {
						id: addonId,
						priceId: productPrice!,
					};
				}),
				freeAddonObjects: ExtraItem[] = this.addonItems
					.filter((item) => item.isFree)
					.map((previewItem) => {
						const addonId = previewItem.id as BillingAddonType,
							productPrice = plan.getAddonProductPrice(addonId);

						return {
							id: addonId,
							priceId: productPrice!,
							isFree: true,
						};
					}),
				serviceObjects: ExtraItem[] = this.serviceItems.map((service) => {
					const productPrice = plan.getServiceProductPrice(
						service.id as BillingServiceType,
					);

					return {
						id: service.id as string,
						priceId: productPrice!,
						isFree: service.isFree,
					};
				});

			this.submitInProgress = true;

			this.popupData
				.onConfirm(
					plan,
					[...selectedAddonObjects, ...freeAddonObjects],
					serviceObjects,
					this.popupData.trackingOrigin,
				)
				.then(() => {
					this.close();
				})
				.finally(() => {
					this.submitInProgress = false;
				})
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		}
	}

	@action
	addItem(item: PreviewItem) {
		const itemId = item.id;
		if (itemId) {
			const selectedAddonIds = this.selectedAddonIds;
			if (selectedAddonIds.includes(itemId)) {
				this.selectedAddonIds = selectedAddonIds.filter((addon) => addon !== itemId);
			} else {
				this.selectedAddonIds = [...selectedAddonIds, itemId];
			}

			this.requestSubscriptionPreviewData();
		}
	}

	// "Change plan" navigates to Plans, so close even while the preview loads
	// (skips the CLOSE_INTENT guard, like notifications/edit/popup forceClose)
	@action
	closeForPlanChange() {
		void this.popupService.close(this.args.model);
	}

	requestSubscriptionPreviewData() {
		if (this.requestInProgress) {
			return;
		}

		const addons = this.selectedAddonIds.map((id) => {
			return { id: id as BillingAddonType, quantity: 1 };
		});

		this.requestInProgress = true;
		this.billingService
			.requestSubscriptionPreview(this.plan.productId, addons, true)
			.then((reply) => {
				this.subcriptionPreview = reply;
			})
			.catch((errorData: RequestError) => {
				this.requestService.handleGenericThrownError(errorData);
				this.close(); // https://app.asana.com/0/1201898938264596/1208124328940462/f
			})
			.finally(() => {
				// Set timeout to prevent cached value to cause dialog to close prematurely
				setTimeout(() => {
					this.requestInProgress = false;
					this.scheduleReposition();
				}, 100);
			});
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Growth" @size="lg" />
			Change subscription
		</div>

		<div class="content">

			<div class="selected-plan selected-plan--{{this.plan.planType}}">
				<CommonUiIconComponent @name="Box" @size="xl" />
				<div class="selected-plan__content">
					<div class="selected-plan__header">
						{{this.plan.name}}
						<CommonUpgradePlanLinkComponent
							@text="Change plan"
							@trackingOrigin={{this.popupData.trackingOrigin}}
							@onLimitationsHandled={{this.closeForPlanChange}}
						/>
					</div>
					<div class="selected-plan__description">
						{{this.plan.description}}
					</div>
				</div>
			</div>

			<div class="row radiobuttons">
				<FormRadiobuttonGroupComponent
					class="radiobutton-switcher"
					@name="billing-plan-popup"
					@selected={{this.planId}}
					@options={{this.pricingOptionsList}}
					@onChange={{this.changePlan}}
					@theme="alternate"
				/>
			</div>

			{{#if this.selectedDiscountPromotionCycleDescription}}
				<div class="extra-promo-discount">
					<CommonUiIconComponent @name="Gift" @size="sm" />
					<p>
						{{this.selectedDiscountPromotionCycleDescription}}
						<span class="savings">
							(Total saving
							{{this.totalSavingsWithDiscountPromotionFormatted}})
						</span>
					</p>
				</div>
			{{/if}}

			<div class="invoice">

				{{#if this.requestInProgress}}
					<div class="loader__wrapper">
						<BarLoaderComponent />
					</div>
				{{else}}
					{{#each this.invoiceItems as |item|}}
						<BillingPreviewItemComponent
							@model={{item}}
							@forBillingCycle={{this.plan.billingCycle}}
						/>
					{{/each}}
				{{/if}}

			</div>

			{{#if this.addonItems.length}}
				<div class="addons">

					<div>Popular add-ons:</div>

					{{#each this.addonItems as |item|}}
						<BillingPreviewItemComponent
							@model={{item}}
							@selectedItemIds={{this.selectedAddonIds}}
							@isLoading={{this.requestInProgress}}
							@forBillingCycle={{this.plan.billingCycle}}
							@onAction={{this.addItem}}
						/>
					{{/each}}

					{{#each this.serviceItems as |item|}}
						<BillingPreviewItemComponent
							@model={{item}}
							@isLoading={{this.requestInProgress}}
							@forBillingCycle={{this.plan.billingCycle}}
						/>
					{{/each}}

				</div>
			{{/if}}

			<div class="summary">
				{{#if this.requestInProgress}}
					<div class="loader__wrapper">
						<BarLoaderComponent />
					</div>
				{{else}}
					<div class="summary__row summary__row--total">
						<div>Total:</div>
						<div><strong>{{this.pricesFormatted.total}}</strong></div>
					</div>
					{{#each this.discountItems as |item|}}
						<div class="summary__row summary__row--proration">
							<div>{{item.name}}</div>
							<div><strong>{{item.priceFormatted}}</strong></div>
						</div>
					{{/each}}
					{{#if this.pricesFormatted.proration}}
						<div class="summary__row summary__row--proration">
							<div>
								Prorate for unused time on current subscription ({{this.formattedTodayDate}}
								-
								{{this.billingService.formattedNextPaymentDate}}):
							</div>
							<div><strong>{{this.pricesFormatted.proration}}</strong></div>
						</div>
					{{/if}}
					{{#if this.pricesFormatted.balance}}
						<div class="summary__row summary__row--balance">
							<div>Applied balance:</div>
							<div><strong>{{this.pricesFormatted.balance}}</strong></div>
						</div>
					{{/if}}
					{{#if this.pricesFormatted.dueNow}}
						<div class="summary__row summary__row--due-now">
							<div>Due now:</div>
							<div>
								<strong>{{this.pricesFormatted.dueNow}}</strong>
							</div>
						</div>
					{{/if}}
				{{/if}}
			</div>

			{{#if this.showBilingCycleChangeText}}

				<p>
					By switching{{#if this.isPeriodChange}} the billing period of{{/if}}
					your base subscription, all active add-ons will be switched to the same billing
					cycle.
				</p>

			{{/if}}

			<div class="next-invoice-date">
				<div>Next invoice date:</div>
				{{#if this.requestInProgress}}
					<div class="loader__wrapper">
						<BarLoaderComponent @size="tiny" />
					</div>
				{{else}}
					<div>{{this.formattedNextPaymentDate}}</div>
				{{/if}}

			</div>

			<p>
				{{#if this.billingService.isPayingCustomer}}
					{{#if this.willProrate}}
						You’ll be charged a pro-rated amount covering the difference in cost between
						plans for the time left in your current billing period.
					{{/if}}
				{{/if}}
				By confirming you agree to our
				<a href="https://databox.com/terms-of-service" target="_blank">Terms of Service</a>.
			</p>

			{{#if this.flowCancellationService.billingPopupConfirmNotificationText}}
				<AppNotificationsPausedOrDiscountedComponent>
					{{this.flowCancellationService.billingPopupConfirmNotificationText}}
				</AppNotificationsPausedOrDiscountedComponent>
			{{/if}}

		</div>

		<div class="controls2 controls2--sticky">
			<div class="controls2__right">
				<SubmitButtonComponent
					@text="Cancel"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{this.close}}
				/>
				<SubmitButtonComponent
					@text="Confirm"
					@onTrigger={{this.confirm}}
					@isDisabled={{this.isConfirmDisabled}}
					@submitInProgress={{this.submitInProgress}}
				/>
			</div>
		</div>
	</template>
}
